import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Search } from "lucide-react";
import api from "@/lib/api";
import { SourceBadge } from "./SourceBadge";

export const GlobalSearch = () => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState(null);
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const boxRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (q.trim().length < 2) { setResults(null); return; }
    setBusy(true);
    const t = setTimeout(() => {
      api.get("/v3/search", { params: { q } })
        .then((r) => setResults(r.data.results))
        .catch(() => setResults([]))
        .finally(() => setBusy(false));
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (r) => {
    setOpen(false); setQ("");
    if (r.collection === "loanbooks") navigate(`/portfolio?borrower=${encodeURIComponent(r.title)}`);
    else if (r.collection === "test_records") navigate("/foundation");
    else navigate("/foundation");
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-md">
      <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-sm px-3 py-1.5 focus-within:border-[#b3122a]/50">
        <Search size={15} className="text-slate-400" />
        <input
          data-testid="global-search-input"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Search borrowers, facilities, V3 records…"
          className="bg-transparent outline-none text-sm w-full placeholder:text-slate-400"
        />
      </div>
      {open && q.trim().length >= 2 && (
        <div data-testid="global-search-results" className="absolute z-50 mt-1 w-full bg-white border border-slate-200 rounded-sm shadow-md max-h-80 overflow-auto">
          {busy && <div className="px-3 py-2.5 text-xs text-slate-400">Searching…</div>}
          {!busy && results && results.length === 0 && (
            <div className="px-3 py-2.5 text-xs text-slate-400">No matches. Not available.</div>
          )}
          {!busy && results && results.map((r, i) => (
            <button key={i} onClick={() => go(r)} data-testid="global-search-result-row"
              className="w-full text-left px-3 py-2 hover:bg-slate-50 flex items-center justify-between gap-2 border-b border-slate-100 last:border-0">
              <div className="min-w-0">
                <div className="text-sm font-semibold text-slate-800 truncate">{r.title}</div>
                <div className="text-[11px] text-slate-400 truncate">{r.type} · {r.subtitle}</div>
              </div>
              <SourceBadge source={r.source === "HISTORICAL" ? "HISTORICAL" : "TEST_V3"} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
